import React, { useCallback, useMemo } from "react";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPenAlt } from "@fortawesome/pro-solid-svg-icons/faPenAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import List from "antd/es/list";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import Dated from "@mapmycustomers/shared/types/base/Dated";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import Named from "@mapmycustomers/shared/types/base/Named";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Popconfirm } from "@mapmycustomers/ui";

import layout from "@app/styles/layout";

import styles from "./ListDrawerItem.module.scss";

interface Props<T> {
  deleteConfirmationText: string;
  disabledTooltip?: React.ReactNode;
  enabledGetter?: (row: T) => boolean;
  item: T;
  onDelete?: (row: T) => void;
  onEdit?: (row: T) => void;
  onSelect: (row: T) => void;
  selectConfirmationConfirmText?: string;
  selectConfirmationText?: string;
  subTitleGetter?: (row: T) => React.ReactNode;
  titleGetter: (row: T) => React.ReactNode;
}

const ListDrawerItem = <T extends Identified & Named & Dated<Date>>({
  deleteConfirmationText,
  disabledTooltip,
  enabledGetter,
  item,
  onDelete,
  onEdit,
  onSelect,
  selectConfirmationConfirmText,
  selectConfirmationText,
  subTitleGetter,
  titleGetter,
}: Props<T>) => {
  const intl = useIntl();

  const enabled = enabledGetter?.(item) ?? true;

  const handleClick = useCallback(() => enabled && onSelect(item), [enabled, item, onSelect]);
  const handleDeleteClick = useCallback(() => onDelete?.(item), [item, onDelete]);
  const handleEditClick = useCallback(() => enabled && onEdit?.(item), [enabled, item, onEdit]);

  const selectConfirmText = useMemo(
    () =>
      selectConfirmationConfirmText ??
      intl.formatMessage({
        id: "listDrawer.selectConfirmation.select",
        defaultMessage: "Got it",
        description: "List Drawer No-confirmation text for a Delete",
      }),
    [intl, selectConfirmationConfirmText]
  );

  return (
    <List.Item
      className={cn(styles.container, { [styles.disabled]: !enabled })}
      extra={
        <Row gutter={layout.spacerM} onClick={stopEvents}>
          {enabled && (
            <Col>
              {onEdit && (
                <FontAwesomeIcon
                  className={styles.renameIcon}
                  icon={faPenAlt}
                  onClick={handleEditClick}
                />
              )}
            </Col>
          )}
          <Col>
            {onDelete && (
              <Popconfirm
                cancelText={intl.formatMessage({
                  id: "listDrawer.deleteConfirmation.no",
                  defaultMessage: "Cancel",
                  description: "List Drawer No-confirmation text for a Delete",
                })}
                description={deleteConfirmationText}
                okText={intl.formatMessage({
                  id: "listDrawer.deleteConfirmation.yes",
                  defaultMessage: "Delete",
                  description: "List Drawer Yes-confirmation text for a Delete",
                })}
                onConfirm={handleDeleteClick}
                placement="left"
              >
                <FontAwesomeIcon className={styles.deleteIcon} icon={faMinusCircle} />
              </Popconfirm>
            )}
          </Col>
        </Row>
      }
      onClick={selectConfirmationText ? undefined : handleClick}
    >
      <Tooltip
        overlayClassName={styles.tooltip}
        placement="bottomLeft"
        title={enabled ? null : disabledTooltip}
        trigger={enabled ? [] : ["hover"]}
      >
        {selectConfirmationText ? (
          <Popconfirm
            cancelText={intl.formatMessage({
              id: "listDrawer.selectConfirmation.no",
              defaultMessage: "Cancel",
              description: "List Drawer No-confirmation text for a Select",
            })}
            description={selectConfirmationText}
            okText={selectConfirmText}
            onConfirm={handleClick}
            placement="top"
          >
            <List.Item.Meta
              className={styles.content}
              description={subTitleGetter && subTitleGetter(item)}
              title={titleGetter(item)}
            />
          </Popconfirm>
        ) : (
          <List.Item.Meta
            className={styles.content}
            description={subTitleGetter && subTitleGetter(item)}
            title={titleGetter(item)}
          />
        )}
      </Tooltip>
    </List.Item>
  );
};

export default ListDrawerItem;
